import { CommonModule } from "@angular/common";
import { Component, DestroyRef, inject, Inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { EventComboTicket } from "@models/combo-ticket";
import { SnackBarService } from "@services/snack-bar.service";
import { ButtonComponent } from "../../../../../../../shared/components/button/button.component";
import { DialogHeaderComponent } from "../../../../../../../shared/components/dialog-header/dialog-header.component";
import { EventTicketBatch } from "../../../../../../../shared/components/modal-tickets/types/ticket-batch.type";
import {
  comboBatchKey,
  comboLoteLabel,
  findBatchIndexByKey,
} from "../../../../../../../shared/helpers/combo-source-batch/combo-source-batch.helper";

export interface ComboEditDialogData {
  combo?: EventComboTicket;
  ticketBatches: EventTicketBatch[];
}

type IndividualOption = {
  key: string;
  id?: number;
  title: string;
  quantity: number;
  batchSaleStart: string | null;
  batchSaleEnd: string | null;
  batchKey: string;
  batchIndex: number;
  batchLabel: string;
  batchId?: number;
};

@Component({
  selector: "app-combo-edit-modal",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonComponent,
    MatDialogModule,
    DialogHeaderComponent,
  ],
  templateUrl: "./combo-edit-modal.component.html",
  styleUrl: "./combo-edit-modal.component.scss",
})
export class ComboEditModalComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<ComboEditModalComponent>);
  private readonly formBuilder = inject(FormBuilder);
  private readonly snackbarService = inject(SnackBarService);
  private readonly destroyRef = inject(DestroyRef);

  combo?: EventComboTicket;
  ticketBatches: EventTicketBatch[] = [];
  individualOptions: IndividualOption[] = [];
  componentQuantities: Record<string, number> = {};

  comboForm = this.formBuilder.group({
    title: ["", Validators.required],
    description: ["", Validators.required],
    quantity: [1, [Validators.required, Validators.min(1)]],
    price: [1, [Validators.required, Validators.min(1)]],
    min_per_buyer: [1, [Validators.required, Validators.min(1)]],
    max_per_buyer: [5, [Validators.required, Validators.min(1)]],
    source_batch_key: ["", Validators.required],
    component_keys: this.formBuilder.nonNullable.control<string[]>(
      [],
      Validators.required,
    ),
  });

  constructor(@Inject(MAT_DIALOG_DATA) public data: ComboEditDialogData) {
    this.combo = data.combo;
    this.ticketBatches = data.ticketBatches ?? [];
  }

  ngOnInit(): void {
    this.individualOptions = this.ticketBatches.flatMap((batch, batchIndex) =>
      (batch.tickets ?? []).map((ticket, ticketIndex) => ({
        key: ticket.id
          ? `id:${ticket.id}`
          : `local:${batchIndex}:${ticketIndex}:${ticket.title}`,
        id: ticket.id ? Number(ticket.id) : undefined,
        title: ticket.title,
        quantity: Number(ticket.quantity),
        batchSaleStart: batch.sale_start,
        batchSaleEnd: batch.sale_end,
        batchKey: comboBatchKey(batch, batchIndex),
        batchIndex,
        batchLabel: comboLoteLabel(batchIndex),
        batchId: batch.id ? Number(batch.id) : undefined,
      })),
    );

    this.comboForm.controls.source_batch_key.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((key) => {
        this.keepSelectionInSelectedLote(key ?? "");
      });

    if (this.combo) {
      const selectedKeys = this.individualOptions
        .filter(
          (option) =>
            this.combo?.component_ticket_ids?.includes(option.id ?? -1) ||
            this.combo?.component_tickets?.some(
              (ticket) =>
                (option.id && ticket.id === option.id) ||
                ticket.title === option.title,
            ),
        )
        .map((option) => option.key);

      const quantities: Record<string, number> = {};
      for (const key of selectedKeys) {
        const option = this.individualOptions.find((item) => item.key === key);
        const saved = this.combo.component_tickets?.find(
          (ticket) =>
            (option?.id && ticket.id === option.id) ||
            ticket.title === option?.title,
        );
        quantities[key] = Math.max(1, Number(saved?.quantity ?? 1));
      }
      this.componentQuantities = quantities;

      this.comboForm.patchValue({
        title: this.combo.title,
        description: this.combo.description,
        quantity: this.combo.quantity,
        price: Number(this.combo.price),
        min_per_buyer: this.combo.min_per_buyer,
        max_per_buyer: this.combo.max_per_buyer,
        source_batch_key: this.initialSourceBatchKey(selectedKeys),
        component_keys: selectedKeys,
      });
    } else if (this.availableLots.length === 1) {
      this.comboForm.controls.source_batch_key.setValue(
        this.availableLots[0].key,
      );
    }
  }

  get availableLots(): Array<{ key: string; label: string; index: number }> {
    return this.ticketBatches
      .map((batch, index) => ({
        key: comboBatchKey(batch, index),
        label: comboLoteLabel(index),
        index,
        tickets: batch.tickets ?? [],
      }))
      .filter((lot) => lot.tickets.length > 0)
      .map(({ key, label, index }) => ({ key, label, index }));
  }

  get optionsInSelectedLote(): IndividualOption[] {
    const key = this.comboForm.controls.source_batch_key.value;
    if (!key) return [];
    return this.individualOptions.filter((option) => option.batchKey === key);
  }

  get selectedLoteLabel(): string {
    const key = this.comboForm.controls.source_batch_key.value;
    return this.availableLots.find((lot) => lot.key === key)?.label ?? "";
  }

  get totalPackageUnits(): number {
    return this.selectedOptions.reduce(
      (total, option) => total + this.unitsFor(option.key),
      0,
    );
  }

  get maxAllowedQuantity(): number {
    const selected = this.selectedOptions;
    if (!selected.length || this.totalPackageUnits < 2) return 0;
    return Math.min(
      ...selected.map((item) =>
        Math.floor(item.quantity / this.unitsFor(item.key)),
      ),
    );
  }

  get limitingTicket(): IndividualOption | null {
    const selected = this.selectedOptions;
    if (selected.length < 1 || this.totalPackageUnits < 2) return null;
    return [...selected].sort(
      (a, b) =>
        Math.floor(a.quantity / this.unitsFor(a.key)) -
        Math.floor(b.quantity / this.unitsFor(b.key)),
    )[0];
  }

  get selectedOptions(): IndividualOption[] {
    const keys = this.comboForm.controls.component_keys.value;
    return this.optionsInSelectedLote.filter((item) => keys.includes(item.key));
  }

  unitsFor(key: string): number {
    return Math.max(1, this.componentQuantities[key] ?? 1);
  }

  toggleTicket(key: string, checked: boolean): void {
    const control = this.comboForm.controls.component_keys;
    const next = checked
      ? [...control.value, key]
      : control.value.filter((item) => item !== key);
    control.setValue(next);
    control.markAsTouched();
    if (checked) {
      this.componentQuantities = {
        ...this.componentQuantities,
        [key]: this.componentQuantities[key] ?? 1,
      };
    } else {
      const { [key]: _removed, ...rest } = this.componentQuantities;
      this.componentQuantities = rest;
    }
    this.clampQuantity();
  }

  isSelected(key: string): boolean {
    return this.comboForm.controls.component_keys.value.includes(key);
  }

  increaseUnits(key: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isSelected(key)) {
      this.toggleTicket(key, true);
    }
    const option = this.individualOptions.find((item) => item.key === key);
    const max = option?.quantity ?? 1;
    this.componentQuantities = {
      ...this.componentQuantities,
      [key]: Math.min(max, this.unitsFor(key) + 1),
    };
    this.clampQuantity();
  }

  decreaseUnits(key: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const current = this.unitsFor(key);
    if (!this.isSelected(key)) return;
    if (current <= 1) return;
    this.componentQuantities = {
      ...this.componentQuantities,
      [key]: current - 1,
    };
    this.clampQuantity();
  }

  private clampQuantity(): void {
    const cap = this.maxAllowedQuantity;
    const quantityControl = this.comboForm.controls.quantity;
    if (cap > 0 && Number(quantityControl.value) > cap) {
      quantityControl.setValue(cap);
    }
  }

  closeModal(): void {
    this.dialogRef.close();
  }

  saveCombo(): void {
    this.comboForm.markAllAsTouched();
    if (!this.comboForm.controls.source_batch_key.value) {
      this.snackbarService.openSnackBar(
        "Selecione o lote deste ingresso combo.",
        "Fechar",
        "error",
      );
      return;
    }
    const selected = this.selectedOptions;
    if (!selected.length || this.totalPackageUnits < 2) {
      this.snackbarService.openSnackBar(
        "Inclua pelo menos 2 ingressos no pacote. Pode ser 2 do mesmo tipo (compre 1, leve 2) ou 1 de cada tipo.",
        "Fechar",
        "error",
      );
      return;
    }
    if (this.comboForm.invalid) {
      this.snackbarService.openSnackBar(
        "Formulário possui dados inválidos.",
        "Fechar",
        "error",
      );
      return;
    }
    const cap = this.maxAllowedQuantity;
    const quantity = Number(this.comboForm.controls.quantity.value);
    if (quantity > cap) {
      this.snackbarService.openSnackBar(
        `A quantidade máxima é ${cap}, limitada pelo estoque dos ingressos selecionados.`,
        "Fechar",
        "error",
      );
      return;
    }

    const withStart = selected
      .map((item) => ({ item, time: Date.parse(item.batchSaleStart ?? "") }))
      .filter((entry) => Number.isFinite(entry.time));
    const withEnd = selected
      .map((item) => ({ item, time: Date.parse(item.batchSaleEnd ?? "") }))
      .filter((entry) => Number.isFinite(entry.time));
    const latestStart = withStart.sort((a, b) => b.time - a.time)[0];
    const earliestEnd = withEnd.sort((a, b) => a.time - b.time)[0];
    const saleStart = latestStart?.item.batchSaleStart ?? null;
    const saleEnd = earliestEnd?.item.batchSaleEnd ?? null;

    const sourceIndex = findBatchIndexByKey(
      this.ticketBatches,
      this.comboForm.controls.source_batch_key.value ?? "",
    );
    const sourceBatch =
      sourceIndex >= 0 ? this.ticketBatches[sourceIndex] : undefined;

    const result: EventComboTicket = {
      ...this.combo,
      title: this.comboForm.controls.title.value!.trim(),
      description: this.comboForm.controls.description.value!.trim(),
      quantity,
      price: Number(this.comboForm.controls.price.value),
      min_per_buyer: Number(this.comboForm.controls.min_per_buyer.value),
      max_per_buyer: Number(this.comboForm.controls.max_per_buyer.value),
      availability: "general public",
      source_batch_id: sourceBatch?.id ? Number(sourceBatch.id) : undefined,
      source_batch_number: sourceIndex >= 0 ? sourceIndex + 1 : undefined,
      component_ticket_ids: selected
        .map((item) => item.id)
        .filter((id): id is number => !!id),
      component_tickets: selected.map((item) => ({
        id: item.id ?? 0,
        title: item.title,
        quantity: this.unitsFor(item.key),
      })),
      sale_start: saleStart,
      sale_end: saleEnd,
    };
    this.dialogRef.close(result);
  }

  private initialSourceBatchKey(selectedKeys: string[]): string {
    if (this.combo?.source_batch_id) {
      const match = this.ticketBatches.findIndex(
        (batch) => Number(batch.id) === Number(this.combo?.source_batch_id),
      );
      if (match >= 0) return comboBatchKey(this.ticketBatches[match], match);
    }
    const firstSelected = this.individualOptions.find((option) =>
      selectedKeys.includes(option.key),
    );
    if (firstSelected) return firstSelected.batchKey;
    return this.availableLots[0]?.key ?? "";
  }

  private keepSelectionInSelectedLote(batchKey: string): void {
    const allowed = new Set(
      this.individualOptions
        .filter((option) => option.batchKey === batchKey)
        .map((option) => option.key),
    );
    const current = this.comboForm.controls.component_keys.value;
    const next = current.filter((key) => allowed.has(key));
    if (next.length !== current.length) {
      this.comboForm.controls.component_keys.setValue(next);
      const quantities: Record<string, number> = {};
      for (const key of next) {
        quantities[key] = this.componentQuantities[key] ?? 1;
      }
      this.componentQuantities = quantities;
      this.clampQuantity();
    }
  }
}
